import React, { useState } from 'react';
import { X, Calendar, Clock, MapPin, Users, DollarSign, Share2, Trophy, MessageSquare, ShieldCheck, CheckCircle2, Copy, Sparkles, Navigation, Shield, Bell, Send, PhoneCall, Pencil, Trash2 } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Match, MatchRSVP, GroupMemberProfile, PaymentRecord, MatchComment, GroupSettings, RSVPStatus } from '../types';
import { TacticalPitch } from './TacticalPitch';
import { TeamPositionSelector } from './TeamPositionSelector';
import { autoBalanceTeams } from '../services/store';
import { NumberInput } from './NumberInput';
import { formatTimeTo12Hour, formatTimeRange, generateSmsReminderUrl, generateWhatsappReminderUrl, requestPushNotificationPermission, sendLocalPushNotification } from '../utils/timeUtils';

interface MatchDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: Match;
  rsvps: MatchRSVP[];
  membersMap: Map<string, GroupMemberProfile>;
  payments: PaymentRecord[];
  comments: MatchComment[];
  groupSettings: GroupSettings;
  activeMember: GroupMemberProfile;
  onUpdateRSVP: (matchId: string, status: RSVPStatus) => void;
  onUpdateTeams: (matchId: string, teamA: any, teamB: any) => void;
  onMarkPayment: (matchId: string, memberProfileId: string, status: 'paid' | 'unpaid') => void;
  onAddComment: (matchId: string, text: string) => void;
  onRecordResult: (matchId: string, teamAScore: number, teamBScore: number, motmMemberId: string) => void;
  onShareMatch: (match: Match) => void;
  onEditMatch: (matchId: string) => void;
  onDeleteMatch: (matchId: string) => void;
  onToggleFeaturedMatch: (matchId: string) => void;
}

export const MatchDetailsModal: React.FC<MatchDetailsModalProps> = ({
  isOpen,
  onClose,
  match,
  rsvps,
  membersMap,
  payments,
  comments,
  groupSettings,
  activeMember,
  onUpdateRSVP,
  onUpdateTeams,
  onMarkPayment,
  onAddComment,
  onRecordResult,
  onShareMatch,
  onEditMatch,
  onDeleteMatch,
  onToggleFeaturedMatch
}) => {
  const [activeTab, setActiveTab] = useState<'info' | 'rsvps' | 'teams' | 'pitch' | 'payments' | 'result' | 'chat'>('info');

  const matchRsvps = rsvps.filter(r => r.matchId === match.id);
  const goingRsvps = matchRsvps.filter(r => r.status === 'going');
  const maybeRsvps = matchRsvps.filter(r => r.status === 'maybe');
  const notGoingRsvps = matchRsvps.filter(r => r.status === 'not_going');
  const waitlistRsvps = matchRsvps.filter(r => r.status === 'waitlist');

  const matchPayments = payments.filter(p => p.matchId === match.id);
  const matchComments = comments.filter(c => c.matchId === match.id);

  // Auto Balance Trigger
  const handleAutoBalance = () => {
    const confirmedMembers = goingRsvps
      .map(r => membersMap.get(r.memberProfileId))
      .filter((m): m is GroupMemberProfile => m !== undefined);

    const balanced = autoBalanceTeams(confirmedMembers, '2-3-1', '2-3-1');
    onUpdateTeams(match.id, balanced.teamA, balanced.teamB);
    setActiveTab('pitch');
  };

  // Direct Messaging / Push Notification State
  const [directPhone, setDirectPhone] = useState(activeMember.phoneNumber || '+8801712345678');
  const [notifSentMsg, setNotifSentMsg] = useState('');

  const handleTriggerPushNotif = async () => {
    const granted = await requestPushNotificationPermission();
    if (granted) {
      sendLocalPushNotification(
        `⚽ Match Alert: ${match.title}`,
        `Kickoff at ${formatTimeTo12Hour(match.startTime)} (${match.venueName}). Make sure you arrive 15 minutes early!`
      );
      setNotifSentMsg('🔔 Push Notification sent!');
      setTimeout(() => setNotifSentMsg(''), 4000);
    }
  };

  // Result Form State
  const [teamAScore, setTeamAScore] = useState(match.result?.teamAScore || 7);
  const [teamBScore, setTeamBScore] = useState(match.result?.teamBScore || 5);
  const [motmWinnerId, setMotmWinnerId] = useState(match.motmWinnerMemberId || activeMember.id);
  const [newCommentText, setNewCommentText] = useState('');
  const [copiedText, setCopiedText] = useState(false);

  const currentRsvp = matchRsvps.find((entry) => entry.memberProfileId === activeMember.id);
  const hasJoinedMatch = currentRsvp?.status === 'going';
  const canDirectJoin = match.visibility === 'public' || match.joinPolicy === 'open_join';
  const shouldRequestAccess = !canDirectJoin;
  const isOrganizer = ['owner', 'admin', 'organizer'].includes(activeMember.role);
  const canManageMatch = match.createdByMemberId === activeMember.id;
  const canViewProtectedMatchArea = hasJoinedMatch || isOrganizer || canManageMatch || match.status === 'completed';

  const handleSaveResult = (e: React.FormEvent) => {
    e.preventDefault();
    onRecordResult(match.id, teamAScore, teamBScore, motmWinnerId);
    // Fire celebratory confetti!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const handlePostComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;
    onAddComment(match.id, newCommentText.trim());
    setNewCommentText('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md">
      <div className="relative w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        
        {/* Header */}
        <div className="p-3.5 sm:p-4 border-b border-slate-800 bg-slate-950 space-y-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-black uppercase text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/30">
                  {match.matchType} Match
                </span>
                <span className="text-[11px] text-slate-400 font-mono">
                  {new Date(match.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
                {match.status === 'completed' && (
                  <span className="text-[11px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30 flex items-center gap-1">
                    <Trophy className="w-3 h-3" /> Completed
                  </span>
                )}
                {match.status === 'awaiting_report' && (
                  <span className="text-[11px] font-bold text-orange-300 bg-orange-500/10 px-2 py-0.5 rounded-full border border-orange-500/30 flex items-center gap-1">
                    <Trophy className="w-3 h-3" /> Awaiting Report
                  </span>
                )}
                <span className="text-[11px] font-bold text-sky-300 bg-sky-500/10 px-2 py-0.5 rounded-full border border-sky-500/30">
                  {match.visibility === 'public' ? 'Public' : match.visibility === 'community' ? 'Community' : 'Private'}
                </span>
              </div>
              <h2 className="text-lg sm:text-2xl font-extrabold text-white leading-tight pr-2">{match.title}</h2>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => onShareMatch(match)}
                className="min-h-[40px] min-w-[40px] p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white transition active:scale-95 touch-manipulation flex items-center justify-center"
                title="Share to WhatsApp"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="min-h-[40px] min-w-[40px] p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition active:scale-95 touch-manipulation flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {match.status === 'completed' && match.result && (
            <div className="bg-slate-900 border-2 border-amber-500/50 px-4 py-2 rounded-2xl flex items-center justify-center gap-3 font-mono self-start w-fit max-w-full">
              <div className="text-right">
                <span className="text-[10px] text-slate-400 block">{match.teamA?.name || 'Team Green'}</span>
                <span className="text-lg font-black text-emerald-400">{match.result.teamAScore}</span>
              </div>
              <span className="text-amber-400 text-sm font-black font-sans">FT</span>
              <div className="text-left">
                <span className="text-[10px] text-slate-400 block">{match.teamB?.name || 'Team Blue'}</span>
                <span className="text-lg font-black text-blue-400">{match.result.teamBScore}</span>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation Bar */}
        <div className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/95 backdrop-blur-md">
          <div className="flex overflow-x-auto no-scrollbar px-2 sm:px-3 py-1.5 gap-1.5 touch-manipulation">
          {[
            { id: 'info', label: 'Summary' },
            ...(canViewProtectedMatchArea ? [
              { id: 'teams', label: match.status === 'completed' ? 'Squad' : 'Squad' },
              { id: 'pitch', label: 'Pitch' },
              { id: 'rsvps', label: `RSVPs (${goingRsvps.length}/${match.maxPlayers})` },
              { id: 'payments', label: 'Money' },
              { id: 'chat', label: `Chat (${matchComments.length})` },
            ] : []),
            { id: 'result', label: match.status === 'completed' ? 'Score' : match.status === 'awaiting_report' ? 'Finish' : 'Result' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              className={`shrink-0 min-h-[38px] px-3 sm:px-4 py-1.5 whitespace-nowrap rounded-xl transition font-bold text-xs touch-manipulation flex items-center justify-center ${
                activeTab === tab.id
                  ? 'bg-emerald-500 text-slate-950 shadow-sm shadow-emerald-500/20'
                  : 'bg-white/[0.03] text-slate-300 hover:text-slate-100 hover:bg-slate-800/80 active:bg-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
          </div>
        </div>

        {/* Tab Contents */}
        <div className="p-3 sm:p-5 overflow-y-auto flex-1 space-y-3">
          
          {/* TAB 1: SUMMARY & INFO */}
          {activeTab === 'info' && (
            <div className="space-y-3">
              {!canViewProtectedMatchArea && match.status !== 'completed' && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-3.5 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-200 font-black">Join gate</div>
                    <h3 className="mt-1 text-sm font-black text-white leading-snug">Join this match to unlock positioning and in-match details</h3>
                    <p className="mt-1.5 text-[11px] text-slate-200 leading-relaxed">After you join a public match—or request access for a private/community one—you’ll unlock team positioning, tactical pitch, finances, and match chat.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canDirectJoin ? (
                      <>
                        <button
                          type="button"
                          onClick={() => onUpdateRSVP(match.id, 'going')}
                          className="min-h-[40px] px-3.5 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition"
                        >
                          Join this match
                        </button>
                        <button
                          type="button"
                          onClick={() => onUpdateRSVP(match.id, 'not_going')}
                          className="min-h-[40px] px-3.5 py-2 bg-white/5 hover:bg-white/10 text-slate-100 font-bold text-xs rounded-xl border border-white/10 transition"
                        >
                          Not now
                        </button>
                      </>
                    ) : (
                      <div className="text-[11px] text-slate-300 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5">
                        This listing needs organizer approval. Use the request flow from the listing card to ask for access.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Result Banner if Completed */}
              {match.status === 'completed' && match.result && (
                <div className="bg-gradient-to-r from-amber-500/20 via-slate-900 to-amber-500/20 border-2 border-amber-500/50 p-4 rounded-2xl text-center space-y-2">
                  <div className="flex items-center justify-center gap-2 text-amber-400 text-xs font-extrabold uppercase">
                    <Trophy className="w-4 h-4" /> Final Score
                  </div>
                  <div className="text-3xl font-black text-white font-mono flex items-center justify-center gap-4">
                    <span>{match.teamA?.name || 'Team Green'} {match.result.teamAScore}</span>
                    <span className="text-amber-400">-</span>
                    <span>{match.result.teamBScore} {match.teamB?.name || 'Team Blue'}</span>
                  </div>
                  {match.motmWinnerMemberId && (
                    <div className="text-xs font-bold text-amber-300">
                      🏆 MOTM: {membersMap.get(match.motmWinnerMemberId)?.displayName}
                    </div>
                  )}
                </div>
              )}

              {canManageMatch && (
                <div className="bg-slate-950 border border-emerald-500/30 p-3.5 rounded-2xl space-y-2.5">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-wider text-emerald-300">Creator Controls</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">Edit, delete, or pin this match as the featured home card.</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onEditMatch(match.id)}
                      className="py-2.5 px-3.5 rounded-xl bg-emerald-500 text-slate-950 font-black text-[11px] flex items-center justify-center gap-1.5"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleFeaturedMatch(match.id)}
                      className="py-2.5 px-3.5 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-300 font-black text-[11px] flex items-center justify-center gap-1.5"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> {match.isFeaturedHome ? 'Unpin Featured' : 'Pin Featured'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteMatch(match.id)}
                      className="py-2.5 px-3.5 rounded-xl bg-red-500/15 border border-red-500/40 text-red-300 font-black text-[11px] flex items-center justify-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              )}

              {/* Grid Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-1.5">
                  <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-emerald-400" /> Match Date & Time
                  </span>
                  <div className="text-[13px] font-black text-white leading-snug">
                    {new Date(match.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="text-[11px] font-mono font-bold text-emerald-400">
                    {formatTimeRange(match.startTime, match.endTime)} ({match.durationMinutes} Mins)
                  </div>
                </div>

                <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-1.5">
                  <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-emerald-400" /> Venue & Location
                  </span>
                  <div className="text-[13px] font-black text-white">{match.venueName}</div>
                  <a
                    href={match.venueMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-blue-400 hover:underline font-semibold"
                  >
                    <span>Open in Google Maps</span>
                    <Navigation className="w-3 h-3" />
                  </a>
                </div>
              </div>

              {/* Direct Messaging & Push Notification Section */}
              <div className="bg-gradient-to-r from-amber-950/30 via-slate-950 to-slate-950 border border-amber-500/30 p-3.5 rounded-2xl space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold text-amber-300 flex items-center gap-1.5">
                    <Bell className="w-3.5 h-3.5 text-amber-400" /> Squad Notifications & Messages
                  </span>
                  {notifSentMsg && (
                    <span className="text-[11px] font-bold text-emerald-400">{notifSentMsg}</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleTriggerPushNotif}
                    className="py-2 px-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded-xl text-[11px] font-bold transition flex items-center gap-1.5"
                  >
                    <Bell className="w-3.5 h-3.5" /> Push Notification
                  </button>

                  <a
                    href={generateSmsReminderUrl(directPhone, match.title, match.date, match.startTime, match.venueName)}
                    className="py-2 px-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/40 rounded-xl text-[11px] font-bold transition flex items-center gap-1.5"
                  >
                    <Send className="w-3.5 h-3.5" /> SMS Alert
                  </a>

                  <a
                    href={generateWhatsappReminderUrl(directPhone, match.title, match.date, match.startTime, match.venueName)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2 px-3 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-xl text-[11px] font-bold transition flex items-center gap-1.5"
                  >
                    <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
              </div>

              {/* Capacity & Cost Breakdown */}
              <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-2.5">
                <div className="flex justify-between items-center text-[11px] font-bold">
                  <span className="text-slate-300">Player Registration ({goingRsvps.length}/{match.maxPlayers})</span>
                  <span className="text-emerald-400 font-mono">
                    {match.maxPlayers - goingRsvps.length > 0
                      ? `${match.maxPlayers - goingRsvps.length} Slots Left`
                      : 'Match Full'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-mono">
                  <div className="bg-slate-900 p-2 rounded-xl">
                    <span className="text-[10px] text-slate-400 block font-sans">Total Turf Cost</span>
                    <span className="font-extrabold text-white">৳{match.totalTurfFee}</span>
                  </div>
                  <div className="bg-slate-900 p-2 rounded-xl">
                    <span className="text-[10px] text-slate-400 block font-sans">Estimated / Person</span>
                    <span className="font-extrabold text-emerald-400">৳{match.estimatedCostPerPlayer}</span>
                  </div>
                  <div className="bg-slate-900 p-2 rounded-xl">
                    <span className="text-[10px] text-slate-400 block font-sans">Waitlist</span>
                    <span className="font-extrabold text-amber-400">{waitlistRsvps.length}</span>
                  </div>
                </div>
              </div>

              {/* Match Rules & Notes */}
              {match.rulesNote && (
                <div className="bg-amber-950/20 border border-amber-500/30 p-3.5 rounded-2xl text-[11px] space-y-1">
                  <div className="font-extrabold text-amber-300 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> Organizer Rules & Notes
                  </div>
                  <p className="text-amber-100/90 leading-relaxed">{match.rulesNote}</p>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: RSVPs LIST */}
          {activeTab === 'rsvps' && canViewProtectedMatchArea && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-extrabold text-white">Confirmed Going ({goingRsvps.length})</h4>
                {isOrganizer && (
                  <button
                    onClick={handleAutoBalance}
                    className="px-3 py-1.5 bg-emerald-500 text-slate-950 text-xs font-black rounded-xl hover:bg-emerald-400 transition"
                  >
                    Auto-Balance Teams ⚡
                  </button>
                )}
              </div>

              {/* Going Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {goingRsvps.map((r) => {
                  const m = membersMap.get(r.memberProfileId);
                  return (
                    <div key={r.id} className="flex items-center justify-between bg-slate-950 p-3 rounded-2xl border border-slate-800">
                      <div className="flex items-center gap-2.5">
                        <img
                          src={m?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
                          alt={m?.displayName}
                          className="w-9 h-9 rounded-full object-cover border border-slate-700"
                        />
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-1.5">
                            <span>{m?.displayName}</span>
                            <span className="text-[10px] bg-emerald-950 text-emerald-400 px-1.5 py-0.2 rounded font-mono">
                              #{m?.jerseyNumber}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-mono">{r.preferredPosition || m?.primaryPosition} • OVR {m?.footballCard?.overall ?? 0}</div>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                        Going
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Maybe List */}
              {maybeRsvps.length > 0 && (
                <div className="pt-3 border-t border-slate-800">
                  <h4 className="text-xs font-bold text-slate-400 mb-2">Maybe ({maybeRsvps.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {maybeRsvps.map(r => {
                      const m = membersMap.get(r.memberProfileId);
                      return (
                        <span key={r.id} className="text-xs bg-slate-950 text-amber-300 border border-slate-800 px-3 py-1 rounded-full font-medium">
                          🟡 {m?.displayName}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: TEAMS & POSITION SELECTOR */}
          {activeTab === 'teams' && canViewProtectedMatchArea && (
            <div className="space-y-5">
              <TeamPositionSelector
                match={match}
                activeMember={activeMember}
                membersMap={membersMap}
                onUpdateTeams={onUpdateTeams}
                onUpdateRSVP={(status) => onUpdateRSVP(match.id, status)}
              />

              {/* Roster Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Team A Roster */}
                <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                    <span className="font-extrabold text-white text-xs flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: match.teamA?.color || '#10B981' }} />
                      {match.teamA?.name || 'Team Green'} Roster
                    </span>
                    <span className="text-[10px] font-mono text-emerald-400 font-bold">
                      {match.teamA?.players.length || 0} Players
                    </span>
                  </div>

                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                    {match.teamA?.players.length ? (
                      match.teamA.players.map((p, idx) => {
                        const mem = membersMap.get(p.memberProfileId);
                        return (
                          <div key={idx} className="flex items-center justify-between bg-slate-900 p-2 rounded-xl text-xs">
                            <div className="flex items-center gap-2">
                              <img src={mem?.avatar} alt={mem?.displayName} className="w-7 h-7 rounded-full object-cover" />
                              <div>
                                <span className="font-bold text-white block leading-tight">{mem?.displayName}</span>
                                <span className="text-[10px] text-slate-400 font-mono">#{mem?.jerseyNumber} • OVR {mem?.footballCard?.overall ?? 0}</span>
                              </div>
                            </div>
                            <span className="bg-emerald-500/20 text-emerald-400 font-black px-2 py-0.5 rounded text-[10px] font-mono border border-emerald-500/30">
                              {p.position}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-slate-500 italic text-center py-2">No players assigned yet</p>
                    )}
                  </div>
                </div>

                {/* Team B Roster */}
                <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                    <span className="font-extrabold text-white text-xs flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: match.teamB?.color || '#3B82F6' }} />
                      {match.teamB?.name || 'Team Blue'} Roster
                    </span>
                    <span className="text-[10px] font-mono text-blue-400 font-bold">
                      {match.teamB?.players.length || 0} Players
                    </span>
                  </div>

                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                    {match.teamB?.players.length ? (
                      match.teamB.players.map((p, idx) => {
                        const mem = membersMap.get(p.memberProfileId);
                        return (
                          <div key={idx} className="flex items-center justify-between bg-slate-900 p-2 rounded-xl text-xs">
                            <div className="flex items-center gap-2">
                              <img src={mem?.avatar} alt={mem?.displayName} className="w-7 h-7 rounded-full object-cover" />
                              <div>
                                <span className="font-bold text-white block leading-tight">{mem?.displayName}</span>
                                <span className="text-[10px] text-slate-400 font-mono">#{mem?.jerseyNumber} • OVR {mem?.footballCard?.overall ?? 0}</span>
                              </div>
                            </div>
                            <span className="bg-blue-500/20 text-blue-400 font-black px-2 py-0.5 rounded text-[10px] font-mono border border-blue-500/30">
                              {p.position}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-slate-500 italic text-center py-2">No players assigned yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: TACTICAL PITCH */}
          {activeTab === 'pitch' && canViewProtectedMatchArea && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-emerald-400 font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Tactical Pitch & Lineups
                </span>
                {isOrganizer && (
                  <button
                    onClick={handleAutoBalance}
                    className="px-3 py-1.5 bg-emerald-500 text-slate-950 text-xs font-black rounded-xl hover:bg-emerald-400 transition"
                  >
                    Auto-Balance Teams ⚡
                  </button>
                )}
              </div>

              <TacticalPitch
                teamA={match.teamA}
                teamB={match.teamB}
                membersMap={membersMap}
                isEditable={isOrganizer}
                onUpdateTeam={(key, updated) => {
                  if (key === 'teamA') onUpdateTeams(match.id, updated, match.teamB);
                  else onUpdateTeams(match.id, match.teamA, updated);
                }}
              />
            </div>
          )}

          {/* TAB 4: PAYMENTS */}
          {activeTab === 'payments' && canViewProtectedMatchArea && (
            <div className="space-y-4">
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex justify-between items-center">
                <div>
                  <span className="text-xs font-bold text-slate-300 block">bKash Payment Number:</span>
                  <span className="text-sm font-mono font-black text-pink-400">{groupSettings.finance.bkashNumber}</span>
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-300 block">Nagad Payment Number:</span>
                  <span className="text-sm font-mono font-black text-orange-400">{groupSettings.finance.nagadNumber}</span>
                </div>
              </div>

              <h4 className="text-xs font-extrabold text-white uppercase tracking-wider">
                Turf Cost Collection Ledger
              </h4>

              <div className="space-y-2">
                {goingRsvps.map((r) => {
                  const m = membersMap.get(r.memberProfileId);
                  const payRec = matchPayments.find(p => p.memberProfileId === r.memberProfileId);
                  const isPaid = payRec?.status === 'paid';

                  return (
                    <div key={r.id} className="flex items-center justify-between bg-slate-950 p-3 rounded-2xl border border-slate-800 text-xs">
                      <div className="flex items-center gap-2.5">
                        <img src={m?.avatar} alt={m?.displayName} className="w-8 h-8 rounded-full object-cover" />
                        <div>
                          <span className="font-bold text-white block">{m?.displayName}</span>
                          <span className="text-[10px] text-slate-400">Share: ৳{match.estimatedCostPerPlayer}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded-full font-bold font-mono text-[10px] ${
                          isPaid ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
                        }`}>
                          {isPaid ? 'PAID' : 'UNPAID'}
                        </span>

                        {isOrganizer && (
                          <button
                            onClick={() => onMarkPayment(match.id, r.memberProfileId, isPaid ? 'unpaid' : 'paid')}
                            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-white text-[10px] font-bold rounded-lg border border-slate-700 transition"
                          >
                            Mark {isPaid ? 'Unpaid' : 'Paid'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 5: RESULT & STATS */}
          {activeTab === 'result' && (
            <form onSubmit={handleSaveResult} className="space-y-4">
              {match.status !== 'completed' && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-4 text-xs text-slate-200 leading-relaxed">
                  Save the score here first. As soon as you submit, GigaBalls will open the full post-match flow so you can enter player stats and publish the completed report.
                </div>
              )}
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 text-center space-y-3">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Match Scoreboard
                </span>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <span className="text-xs text-slate-300 font-bold block mb-1">{match.teamA?.name || 'Team Green'}</span>
                    <NumberInput
                      value={teamAScore}
                      onCommit={setTeamAScore}
                      min={0}
                      className="w-16 h-14 bg-slate-900 border-2 border-emerald-500 text-center font-black text-2xl text-emerald-400 rounded-xl focus:outline-none"
                      aria-label="Team A score"
                    />
                  </div>
                  <span className="text-2xl font-black text-slate-500">-</span>
                  <div className="text-center">
                    <span className="text-xs text-slate-300 font-bold block mb-1">{match.teamB?.name || 'Team Blue'}</span>
                    <NumberInput
                      value={teamBScore}
                      onCommit={setTeamBScore}
                      min={0}
                      className="w-16 h-14 bg-slate-900 border-2 border-blue-500 text-center font-black text-2xl text-blue-400 rounded-xl focus:outline-none"
                      aria-label="Team B score"
                    />
                  </div>
                </div>
              </div>

              {/* MOTM Winner Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">
                  🏆 Select Man of the Match (MOTM) Winner
                </label>
                <select
                  value={motmWinnerId}
                  onChange={(e) => setMotmWinnerId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-amber-300 font-bold rounded-xl p-3 text-sm focus:outline-none"
                >
                  {goingRsvps.map((r) => {
                    const m = membersMap.get(r.memberProfileId);
                    return (
                      <option key={r.id} value={r.memberProfileId}>
                        🏆 {m?.displayName} ({m?.primaryPosition})
                      </option>
                    );
                  })}
                </select>
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm rounded-xl transition shadow-lg shadow-amber-500/20"
              >
                {match.status === 'completed' ? 'Update Score & Celebrate MOTM 🎉' : 'Save Score & Continue to Player Stats →'}
              </button>
            </form>
          )}

          {/* TAB 6: CHAT / COMMENTS */}
          {activeTab === 'chat' && canViewProtectedMatchArea && (
            <div className="space-y-4">
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {matchComments.map((c) => (
                  <div key={c.id} className={`p-3 rounded-2xl text-xs space-y-1 ${c.isPinned ? 'bg-amber-950/30 border border-amber-500/40' : 'bg-slate-950 border border-slate-800'}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-emerald-400">{c.authorName}</span>
                      <span className="text-[10px] text-slate-500 font-mono">{c.timestamp}</span>
                    </div>
                    <p className="text-slate-200">{c.text}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handlePostComment} className="flex gap-2">
                <input
                  type="text"
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  placeholder="Ask a question or comment..."
                  className="flex-1 bg-slate-950 border border-slate-700 text-white text-xs rounded-xl px-3 py-2.5 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl transition"
                >
                  Send
                </button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
